import {
  AnyTriggerTransition,
  getValueByTransitionPath,
  isComplexWhenPredicate,
  isPlaceHolderStepId,
  isWhenUserResponse,
  TriggerStepCreate,
  WhenUserResponse,
} from "@dots-projects/types";
import { Button, Input, Select, Spin } from "antd";
import produce from "immer";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { FormLabel } from "./FormLabel";
import { deleteSteps, saveDraft, updateStep } from "./reducer/actionCreators";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";

// branch types for selector
const kindsOfBranches = [
  {
    id: 1,
    label: "Wait for a text response",
    value: "waitForResponse",
    disabled: false,
  },
];

// conditions for selector
const conditions = [
  {
    id: 1,
    label: "-",
    value: "noCondition",
    disabled: false,
  },
  {
    id: 2,
    label: "Wait for an email address",
    value: "waitForEmail",
    disabled: false,
  },
  {
    id: 3,
    label: "Wait for a first name",
    value: "waitForName",
    disabled: false,
  },
  {
    id: 4,
    label: "Wait for a number",
    value: "waitForNumber",
    disabled: false,
  },
  {
    id: 5,
    label: "Wait for age",
    value: "waitForAge",
    disabled: false,
  },
  {
    id: 6,
    label: "Wait for a custom matching regex",
    value: "waitForRegex",
    disabled: false,
  },
  {
    id: 7,
    label: "Wait for a match word",
    value: "waitForMatchWord",
    disabled: false,
  },
  {
    id: 8,
    label: "Wait for captcha passes",
    value: "waitForCaptchaPasses",
    disabled: true,
  },
  {
    id: 9,
    label: "Wait for captcha fails",
    value: "waitForCaptchaFails",
    disabled: true,
  },
  {
    id: 10,
    label: "Skip",
    value: "skip",
    disabled: false,
  },
];

type Props = {
  idx: number;
  step: TriggerStepCreate;
  transition: AnyTriggerTransition;
  readonly?: boolean;
};

// this function returns the initial condition for the "condition" field. The default is "noCondition".
// If the transitions has already a condition, this value is taken.
const getInitialCondition = (transition: AnyTriggerTransition) => {
  if (
    "when" in transition &&
    transition.when &&
    isWhenUserResponse(transition.when)
  ) {
    if (
      transition.when.userResponse.type === "message" &&
      transition.when.userResponse.content
    ) {
      if (isComplexWhenPredicate(transition.when.userResponse.content)) {
        if (transition.when.userResponse.content.has === "first-name") {
          return "waitForName";
        }
        if (transition.when.userResponse.content.has === "email") {
          return "waitForEmail";
        }
        if (transition.when.userResponse.content.regex === "^[0-9]*$") {
          return "waitForNumber";
        }
        if (transition.when.userResponse.content.regex === "[1-9][0-9]") {
          return "waitForAge";
        }
        if (transition.when.userResponse.content.regex) {
          return "waitForRegex";
        }
        if (
          typeof transition.when.userResponse.content === "string" ||
          transition.when.userResponse.content.eq
        ) {
          return "waitForMatchWord";
        }
      } else if (transition.when.userResponse.content === "skip") {
        return "skip";
      }
    } else if (transition.when.userResponse.type === "captcha-result") {
      if (transition.when.userResponse.succeeded) {
        return "waitForCaptchaPasses";
      }
      return "waitForCaptchaFails";
    }
  }
  return "noCondition";
};

// this function returns the object "when" of the transition based on the "condition"
// this function is used every time a branch of type userResponse is updated.
const getNewWhenObject = (
  condition: string,
  value: string,
  step: TriggerStepCreate
): WhenUserResponse => {
  let channelId = "$channelId";
  if (step.configs && "channelId" in step.configs && step.configs.channelId) {
    channelId = step.configs.channelId;
  }

  switch (condition) {
    case "waitForName":
      return {
        userResponse: {
          type: "message",
          userId: "{{user.id}}",
          channelId,
          content: {
            has: "first-name",
          },
        },
      };
    case "waitForEmail":
      return {
        userResponse: {
          type: "message",
          userId: "{{user.id}}",
          channelId,
          content: {
            has: "email",
          },
        },
      };
    case "waitForNumber":
      return {
        userResponse: {
          type: "message",
          userId: "{{user.id}}",
          channelId,
          content: {
            regex: "^[0-9]*$",
          },
        },
      };
    case "waitForAge":
      return {
        userResponse: {
          type: "message",
          userId: "{{user.id}}",
          channelId,
          content: {
            regex: "[1-9][0-9]",
          },
        },
      };
    case "waitForRegex":
      return {
        userResponse: {
          type: "message",
          userId: "{{user.id}}",
          channelId,
          content: {
            regex: value,
          },
        },
      };
    case "waitForMatchWord":
      return {
        userResponse: {
          type: "message",
          userId: "{{user.id}}",
          channelId,
          content: {
            eq: value,
          },
        },
      };
    case "skip":
      return {
        userResponse: {
          type: "message",
          userId: "{{user.id}}",
          channelId,
          content: "skip",
        },
      };
    default:
      return {
        userResponse: {
          type: "message",
          userId: "{{user.id}}",
          channelId,
        },
      };
  }
};

// this function returns the initial condition for the name field. The default is "Branch <number>".
// If the transitions has a name, this value is taken.
const getName = (transition: AnyTriggerTransition, idx: number) => {
  return transition.name ? transition.name : `Branch ${idx + 1}`;
};

export function BranchForm({ idx, step, transition, readonly }: Props) {
  const { state, dispatch, onPersist } = useTriggerEditorStateDispatch();
  const readOnly = !state.trigger.current.isDraft || readonly;
  const isPersisting = state.editor.isPersisting === "save-draft";
  // Form initial values
  const defaultValues = {
    name: getName(transition, idx),
    type: "waitForResponse",
    condition: getInitialCondition(transition),
    content:
      getValueByTransitionPath(transition, "when.userResponse.content") ||
      getValueByTransitionPath(transition, "when.userResponse.content.eq") ||
      getValueByTransitionPath(transition, "when.userResponse.content.regex"),
  };
  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues,
  });

  const updateBranch = (data: any) => {
    const newStep = produce(step, (stepDraft) => {
      if (data.type === "waitForResponse") {
        //update name and "when" object based on user selections
        stepDraft.transitions[idx].name = data.name;
        stepDraft.transitions[idx].when = getNewWhenObject(
          data.condition,
          //Remove initial / and end / from regex
          data.type === "waitForRegex"
            ? data.content.replace(/^\/|\/$/g, "")
            : data.content,
          step
        );
      }
    });
    dispatch(updateStep(newStep));
    dispatch({ type: "unselect" });
    // save changes
    dispatch(saveDraft(onPersist));
  };

  // target step will only be deleted if it's a placeholder step
  const deleteBranch = () => {
    // delete current transition
    const newStep = produce(step, (stepDraft) => {
      stepDraft.transitions.splice(idx, 1);
    });
    dispatch(updateStep(newStep));
    dispatch({ type: "unselect" });
    dispatch(saveDraft(onPersist));
  };

  return (
    <form
      className="flex flex-col gap-3 text-left"
      onSubmit={handleSubmit(updateBranch)}
    >
      <Controller
        control={control}
        name="name"
        render={({ field: { onChange, onBlur, value } }) => (
          <FormLabel label="Branch name">
            <Input
              type="text"
              style={{ width: "100%" }}
              readOnly={readOnly}
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              disabled={readonly}
            />
          </FormLabel>
        )}
      />
      <Controller
        control={control}
        name="type"
        render={({ field: { onChange, onBlur, value } }) => (
          <FormLabel label="Type">
            <Select
              className="w-full"
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              disabled
            >
              {kindsOfBranches.map((option) => (
                <Select.Option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </FormLabel>
        )}
      />
      {watch("type") === "waitForResponse" && (
        <Controller
          control={control}
          name="condition"
          render={({ field }) => (
            <FormLabel label="Condition">
              <Select
                {...field}
                onChange={(e) => {
                  //Clean content field when condition changes
                  setValue("content", "");
                  field.onChange(e);
                }}
                className="w-full"
                disabled={readonly}
              >
                {conditions.map((option) => (
                  <Select.Option
                    key={option.id}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </FormLabel>
          )}
        />
      )}
      {watch("type") === "waitForResponse" &&
        watch("condition") === "waitForRegex" && (
          <Controller
            control={control}
            name="content"
            render={({ field: { onChange, onBlur, value } }) => (
              <FormLabel label="Custom regex">
                <Input
                  style={{ width: "100%" }}
                  readOnly={readOnly}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  prefix={<p style={{ margin: "0", color: "#878787" }}>/</p>}
                  suffix={<p style={{ margin: "0", color: "#878787" }}>/</p>}
                />
              </FormLabel>
            )}
          />
        )}

      {watch("type") === "waitForResponse" &&
        watch("condition") === "waitForMatchWord" && (
          <Controller
            control={control}
            name="content"
            render={({ field: { onChange, onBlur, value } }) => (
              <FormLabel label="Match word">
                <Input
                  style={{ width: "100%" }}
                  readOnly={readOnly}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                />
              </FormLabel>
            )}
          />
        )}
      <div className="flex flex-col gap-3" style={{ marginTop: "30px" }}>
        {!readOnly && (
          <>
            {transition ? (
              <Button onClick={deleteBranch}>
                <p>
                  Delete{" "}
                  <b>
                    {transition.name ? transition.name : `Branch ${idx + 1}`}
                  </b>
                </p>
              </Button>
            ) : null}
            <Button type="primary" htmlType="submit" disabled={isPersisting}>
              <div className="flex gap-4 justify-center w-full">
                {isPersisting && <Spin />}
                <span>Save Branch</span>
              </div>
            </Button>
          </>
        )}
        {/* following line is for debugging */}
        {/* <p>{JSON.stringify(watch(), null, 2)}</p> */}
      </div>
    </form>
  );
}
